import React, { useState } from 'react';

export interface FiltrosFechaProps {
  onFiltroChange: (filtros: FiltrosFechaType) => void;
  filtroActual?: string;
}

export interface FiltrosFechaType {
  periodo?: string;
  fechaDesde?: Date;
  fechaHasta?: Date;
}

const FiltrosFecha: React.FC<FiltrosFechaProps> = ({ onFiltroChange, filtroActual = 'mes_actual' }) => {
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState(filtroActual);
  const [mesDesde, setMesDesde] = useState<string>('');
  const [añoDesde, setAñoDesde] = useState<string>('');
  const [mesHasta, setMesHasta] = useState<string>('');
  const [añoHasta, setAñoHasta] = useState<string>('');
  const [mostrarRangoPersonalizado, setMostrarRangoPersonalizado] = useState(false);

  const filtrosPredefinidos = [
    { value: 'mes_actual', label: 'Vigente en Mes Actual', icon: '📅' },
    { value: 'mes_anterior', label: 'Vigente al Mes Pasado', icon: '📆' },
    { value: 'semestre_actual', label: 'Vigente Semestre Actual', icon: '📊' },
    { value: 'semestre_anterior', label: 'Vigente al Semestre Pasado', icon: '📈' },
    { value: 'año_actual', label: 'Vigente en Año Actual', icon: '📋' },
    { value: 'año_anterior', label: 'Vigente al Año Pasado', icon: '📄' },
    { value: 'personalizado', label: 'Rango Personalizado', icon: '🎯' }
  ];

  // Función para calcular las fechas que corresponden a cada filtro
  const getFechasFiltro = (periodo: string) => {
    const ahora = new Date();
    
    switch (periodo) {
      case 'mes_actual':
        const inicioMesActual = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        const finMesActual = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0);
        return {
          desde: inicioMesActual.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          hasta: finMesActual.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      
      case 'mes_anterior':
        const inicioMesAnterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
        const finMesAnterior = new Date(ahora.getFullYear(), ahora.getMonth(), 0);
        return {
          desde: inicioMesAnterior.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          hasta: finMesAnterior.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      
      case 'semestre_actual':
        const semestreActual = Math.floor((ahora.getMonth()) / 6) + 1;
        const mesInicioSemestre = (semestreActual - 1) * 6;
        const inicioSemestre = new Date(ahora.getFullYear(), mesInicioSemestre, 1);
        const finSemestre = new Date(ahora.getFullYear(), mesInicioSemestre + 5, new Date(ahora.getFullYear(), mesInicioSemestre + 6, 0).getDate());
        return {
          desde: inicioSemestre.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          hasta: finSemestre.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      
      case 'semestre_anterior':
        const semestreAnterior = Math.floor((ahora.getMonth()) / 6);
        let inicioSemestreAnterior, finSemestreAnterior;
        
        if (semestreAnterior === 0) {
          // Si estamos en el primer semestre, el anterior es el segundo semestre del año anterior
          inicioSemestreAnterior = new Date(ahora.getFullYear() - 1, 6, 1); // Julio del año anterior
          finSemestreAnterior = new Date(ahora.getFullYear() - 1, 11, 31); // Diciembre del año anterior
        } else {
          // El semestre anterior es el primer semestre del año actual
          inicioSemestreAnterior = new Date(ahora.getFullYear(), 0, 1); // Enero del año actual
          finSemestreAnterior = new Date(ahora.getFullYear(), 5, 30); // Junio del año actual
        }
        
        return {
          desde: inicioSemestreAnterior.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          hasta: finSemestreAnterior.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      
      case 'año_actual':
        const inicioAño = new Date(ahora.getFullYear(), 0, 1);
        const finAño = new Date(ahora.getFullYear(), 11, 31);
        return {
          desde: inicioAño.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          hasta: finAño.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      
      case 'año_anterior':
        const inicioAñoAnterior = new Date(ahora.getFullYear() - 1, 0, 1);
        const finAñoAnterior = new Date(ahora.getFullYear() - 1, 11, 31);
        return {
          desde: inicioAñoAnterior.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          hasta: finAñoAnterior.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      
      default:
        return { desde: '', hasta: '' };
    }
  };

  const handlePeriodoChange = (periodo: string) => {
    setPeriodoSeleccionado(periodo);
    
    if (periodo === 'personalizado') {
      setMostrarRangoPersonalizado(true);
    } else {
      setMostrarRangoPersonalizado(false);
      onFiltroChange({ periodo });
    }
  };

  const handleRangoPersonalizado = () => {
    if (mesDesde && añoDesde && mesHasta && añoHasta) {
      const fechaDesde = new Date(parseInt(añoDesde), parseInt(mesDesde) - 1, 1);
      const fechaHasta = new Date(parseInt(añoHasta), parseInt(mesHasta), 0);
      onFiltroChange({
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta
      });
    }
  };

  const getMesActual = () => {
    const hoy = new Date();
    return (hoy.getMonth() + 1).toString().padStart(2, '0');
  };

  const getAñoActual = () => {
    const hoy = new Date();
    return hoy.getFullYear().toString();
  };

  const getMesHace3Meses = () => {
    const hace3Meses = new Date();
    hace3Meses.setMonth(hace3Meses.getMonth() - 3);
    return (hace3Meses.getMonth() + 1).toString().padStart(2, '0');
  };

  const getAñoHace3Meses = () => {
    const hace3Meses = new Date();
    hace3Meses.setMonth(hace3Meses.getMonth() - 3);
    return hace3Meses.getFullYear().toString();
  };

  return (
    <div className="filtros-fecha">
      <div className="filtros-rapidos">
        {filtrosPredefinidos.map((filtro) => {
          const fechas = getFechasFiltro(filtro.value);
          return (
            <button
              key={filtro.value}
              className={`filtro-btn ${periodoSeleccionado === filtro.value ? 'activo' : ''}`}
              onClick={() => handlePeriodoChange(filtro.value)}
              title={fechas.desde && fechas.hasta ? `${fechas.desde} - ${fechas.hasta}` : ''}
            >
              <span className="filtro-icon">{filtro.icon}</span>
              <span className="filtro-label">{filtro.label}</span>
              {fechas.desde && fechas.hasta && (
                <div className="filtro-fechas-indicator">
                  <small>{fechas.desde} - {fechas.hasta}</small>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {mostrarRangoPersonalizado && (
        <div className="filtros-avanzados">
          <div className="rango-fechas">
            <div className="fecha-input">
              <label htmlFor="mes-desde">Desde (Mes/Año):</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select
                  id="mes-desde"
                  value={mesDesde}
                  onChange={(e) => setMesDesde(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">Mes</option>
                  <option value="01">Enero</option>
                  <option value="02">Febrero</option>
                  <option value="03">Marzo</option>
                  <option value="04">Abril</option>
                  <option value="05">Mayo</option>
                  <option value="06">Junio</option>
                  <option value="07">Julio</option>
                  <option value="08">Agosto</option>
                  <option value="09">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
                <select
                  id="año-desde"
                  value={añoDesde}
                  onChange={(e) => setAñoDesde(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">Año</option>
                  {Array.from({ length: 10 }, (_, i) => {
                    const año = new Date().getFullYear() - 5 + i;
                    return (
                      <option key={año} value={año.toString()}>
                        {año}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            <div className="fecha-input">
              <label htmlFor="mes-hasta">Hasta (Mes/Año):</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select
                  id="mes-hasta"
                  value={mesHasta}
                  onChange={(e) => setMesHasta(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">Mes</option>
                  <option value="01">Enero</option>
                  <option value="02">Febrero</option>
                  <option value="03">Marzo</option>
                  <option value="04">Abril</option>
                  <option value="05">Mayo</option>
                  <option value="06">Junio</option>
                  <option value="07">Julio</option>
                  <option value="08">Agosto</option>
                  <option value="09">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
                <select
                  id="año-hasta"
                  value={añoHasta}
                  onChange={(e) => setAñoHasta(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">Año</option>
                  {Array.from({ length: 10 }, (_, i) => {
                    const año = new Date().getFullYear() - 5 + i;
                    return (
                      <option key={año} value={año.toString()}>
                        {año}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            <button 
              className="aplicar-rango-btn"
              onClick={handleRangoPersonalizado}
              disabled={!mesDesde || !añoDesde || !mesHasta || !añoHasta}
            >
              ✅ Aplicar
            </button>
          </div>
          
          <div className="sugerencias-fecha">
            <button 
              className="sugerencia-btn"
              onClick={() => {
                setMesDesde(getMesHace3Meses());
                setAñoDesde(getAñoHace3Meses());
                setMesHasta(getMesActual());
                setAñoHasta(getAñoActual());
              }}
            >
              Últimos 3 meses
            </button>
            <button 
              className="sugerencia-btn"
              onClick={() => {
                setMesDesde('01');
                setAñoDesde(getAñoActual());
                setMesHasta(getMesActual());
                setAñoHasta(getAñoActual());
              }}
            >
              Año actual
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiltrosFecha; 